// @ts-check

/**
 * Per-letter highlight codes.
 *
 * The wordlist API already returns a `_mod` string alongside every word —
 * one character per letter — describing why that letter matched the search
 * (`r` regular, `s` matched `starts`, `c` matched `contains`, `e` matched
 * `ends`). The Wordle endpoint returns all `r`, so the two codes below are
 * synthesised client-side by `getWordleMod` in that same shape, which keeps
 * a single render path for API- and client-supplied mods alike.
 */
export const WORDLE_MOD = {
	CORRECT: "g", // A letter in the correct position, from the `correct` param
	PRESENT: "y", // A letter known to be in the word, from the `includes` param
	REGULAR: "r",
};

/**
 * @param {String} [char]
 * @returns {Boolean} Whether the character is a single a-z letter
 */
const isLetter = (char) => /^[a-z]$/.test(char || "");

/**
 * Counts the occurrences of each letter in a string.
 * @param {String} letters
 * @returns {Object<String, Number>} A letter-keyed tally
 */
const countLetters = (letters) =>
	letters.split("").reduce((counts, letter) => {
		counts[letter] = (counts[letter] || 0) + 1;
		return counts;
	}, {});

/**
 * Builds a per-letter highlight mod string for a Wordle result word.
 *
 * Positions fixed by `correct` win over `includes`, and `includes` is
 * occurrence-counted, so `includes=ee` marks two letters and `includes=e`
 * marks one.
 *
 * A letter fixed by `correct` does *not* use up its `includes` count, even
 * though the API's filter treats it as satisfying the requirement — searching
 * `correct=_le_p&includes=e` returns the same four words as `correct=_le_p`
 * alone. Mirroring the filter here left the second `e` of `sleep` unmarked,
 * which reads as though that `e` were not in the word at all. The
 * highlighting describes the word in front of the user rather than the query
 * that found it, so every occurrence the user asked about gets marked.
 *
 * A consequence: the `includes` count can go unspent, when every occurrence
 * of the letter is already fixed in place. That is correct — there is nothing
 * left to point at.
 *
 * @param {String} word The result word
 * @param {String} [correctTiles] The `correct` param, `_` for unknown positions
 * @param {String} [includes] The `includes` param, which may repeat a letter
 * @returns {String} A mod string the same length as `word`
 */
export const getWordleMod = (word, correctTiles = "", includes = "") => {
	if (typeof word !== "string" || !word) {
		return "";
	}

	const letters = word.toLowerCase().split("");
	const correct = (
		typeof correctTiles === "string" ? correctTiles : ""
	).toLowerCase();

	const mods = letters.map((letter, i) =>
		isLetter(correct[i]) && correct[i] === letter
			? WORDLE_MOD.CORRECT
			: WORDLE_MOD.REGULAR
	);

	const budget = countLetters(
		(typeof includes === "string" ? includes : "")
			.toLowerCase()
			.replace(/[^a-z]/g, "")
	);

	letters.forEach((letter, i) => {
		if (mods[i] === WORDLE_MOD.REGULAR && budget[letter] > 0) {
			mods[i] = WORDLE_MOD.PRESENT;
			budget[letter] -= 1;
		}
	});

	return mods.join("");
};

/**
 * Groups a word into runs of consecutive letters sharing the same mod, so
 * the renderer can emit one element per run rather than one per letter.
 *
 * Vocabulary-agnostic: this works just as well on the `r`/`s`/`c`/`e` mods
 * the wordlist API returns for the non-Wordle result pages. Letters beyond
 * the end of `mod` fall back to regular.
 *
 * @param {String} word
 * @param {String} [mod] One mod character per letter of `word`
 * @returns {{ text: String, mod: String }[]} Consecutive same-mod runs
 */
export const getHighlightSegments = (word, mod = "") => {
	if (typeof word !== "string" || !word) {
		return [];
	}

	const mods = typeof mod === "string" ? mod : "";

	return word.split("").reduce((segments, char, i) => {
		const charMod = mods[i] || WORDLE_MOD.REGULAR;
		const previous = segments[segments.length - 1];

		if (previous && previous.mod === charMod) {
			previous.text += char;
		} else {
			segments.push({ text: char, mod: charMod });
		}

		return segments;
	}, []);
};
